/**
 * Progress Tracker component - tracks tutorial progress in localStorage
 */

import { useEffect, useState, useCallback } from "react";
import { getJSON, pruneNamespace, setJSON } from "#app/utils/storage.client";

export interface TutorialProgress {
  slug: string;
  currentStep: number;
  completedSteps: string[];
  completedCheckpoints: string[];
  lastAccessed: number;
  timeSpent: number;
  completed: boolean;
  started: boolean;
  startedAt?: number;
  completedAt?: number;
}

const STORAGE_PREFIX = "tutorial:";
const STORAGE_NAMESPACE = "tutorial";
const MAX_TUTORIAL_ENTRIES = 200;
const MAX_TUTORIAL_AGE_MS = 180 * 24 * 60 * 60 * 1000; // 180 days

let didBootstrapTutorialStorage = false;

function bootstrapTutorialStorageIndex() {
  if (didBootstrapTutorialStorage) return;
  if (typeof window === "undefined") return;
  didBootstrapTutorialStorage = true;

  // Migrate legacy plain-JSON entries to TTL-wrapped entries with meta,
  // so pruning can safely preserve completed tutorials.
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;

      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as any;
        const value =
          parsed && typeof parsed === "object" && "v" in parsed
            ? parsed.v
            : parsed;
        const completed = Boolean(value?.completed);

        // Rewrite with meta so keepIfMeta works.
        setJSON(key, value as TutorialProgress, {
          namespace: STORAGE_NAMESPACE,
          meta: { completed },
          maxEntries: MAX_TUTORIAL_ENTRIES,
          maxAgeMs: MAX_TUTORIAL_AGE_MS,
          keepIfMeta: (meta) => meta?.completed === true,
        });
      } catch {
        // ignore malformed entries
      }
    }
  } catch {
    // ignore enumeration failures
  }
}

export function useTutorialProgress(slug: string) {
  const [progress, setProgress] = useState<TutorialProgress | null>(null);

  useEffect(() => {
    const key = `${STORAGE_PREFIX}${slug}`;
    bootstrapTutorialStorageIndex();
    // Keep storage bounded. Never prune completed tutorials.
    pruneNamespace({
      namespace: STORAGE_NAMESPACE,
      maxEntries: MAX_TUTORIAL_ENTRIES,
      maxAgeMs: MAX_TUTORIAL_AGE_MS,
      keepIfMeta: (meta) => meta?.completed === true,
    });

    const saved = getJSON<TutorialProgress>(key, {
      namespace: STORAGE_NAMESPACE,
    });
    if (saved) {
      setProgress(saved);
      return;
    }

    // Initialize progress
    const initial: TutorialProgress = {
      slug,
      currentStep: 0,
      completedSteps: [],
      completedCheckpoints: [],
      lastAccessed: Date.now(),
      timeSpent: 0,
      completed: false,
      started: false,
    };
    setProgress(initial);
    setJSON(key, initial, {
      namespace: STORAGE_NAMESPACE,
      meta: { completed: false },
      maxEntries: MAX_TUTORIAL_ENTRIES,
      maxAgeMs: MAX_TUTORIAL_AGE_MS,
      keepIfMeta: (meta) => meta?.completed === true,
    });
  }, [slug]);

  const updateProgress = useCallback(
    (
      updates:
        | Partial<TutorialProgress>
        | ((prev: TutorialProgress) => Partial<TutorialProgress>),
    ) => {
      setProgress((prev) => {
        if (!prev) return prev;
        const partial = typeof updates === "function" ? updates(prev) : updates;
        const newProgress: TutorialProgress = {
          ...prev,
          ...partial,
          lastAccessed: Date.now(),
        };
        const key = `${STORAGE_PREFIX}${slug}`;
        setJSON(key, newProgress, {
          namespace: STORAGE_NAMESPACE,
          meta: { completed: newProgress.completed },
          maxEntries: MAX_TUTORIAL_ENTRIES,
          maxAgeMs: MAX_TUTORIAL_AGE_MS,
          keepIfMeta: (meta) => meta?.completed === true,
        });
        return newProgress;
      });
    },
    [slug],
  );

  const markStepCompleted = useCallback(
    (stepId: string) => {
      updateProgress((prev) => {
        const already = prev.completedSteps.includes(stepId);
        return {
          completedSteps: already
            ? prev.completedSteps
            : [...prev.completedSteps, stepId],
        };
      });
    },
    [updateProgress],
  );

  const markCheckpointCompleted = useCallback(
    (checkpointId: string) => {
      updateProgress((prev) => {
        const already = prev.completedCheckpoints.includes(checkpointId);
        return {
          completedCheckpoints: already
            ? prev.completedCheckpoints
            : [...prev.completedCheckpoints, checkpointId],
        };
      });
    },
    [updateProgress],
  );

  const markCompleted = useCallback(() => {
    updateProgress({
      completed: true,
      completedAt: Date.now(),
    });
  }, [updateProgress]);

  const markStarted = useCallback(() => {
    updateProgress((prev) => ({
      started: true,
      startedAt: prev.startedAt || Date.now(),
    }));
  }, [updateProgress]);

  return {
    progress,
    updateProgress,
    markStepCompleted,
    markCheckpointCompleted,
    markCompleted,
    markStarted,
  };
}
